import {
  Col,
  ErrorSummary,
  FileUploadPattern,
  FormRadioGroup,
  Heading,
  InPageAlert,
  Row,
  SectionPage,
} from '@snsw/react-component-library';
import { IconDownload } from '@snsw/react-component-library/build/Icons/system';
import { PlantDesignDesignerVerifierPage } from 'plantDesign/constants/plantDesignDesignerVerifierPage';
import { storeDesignerVerifierDetails } from 'plantDesign/redux/plantDesignDesignerVerifierSlice';

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { radioOptionsYesNo } from 'shared/constants/constants';
import { getAttachmentApi } from 'shared/utils/Helpers';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import { PersonDetails } from './components/PersonDetails';
import styles from '../Page.module.scss';
import { PagePath } from 'shared/constants/pagePath';
import { useNavigate } from 'react-router-dom';
import { PasswordlessContinueSaveExitButtons } from 'shared/components/formNavigationButtons/PasswordlessContinueSaveExitButtons';
import { saveApplication } from 'shared/redux/applicationSlice';
import { PlantDesignConstants } from 'plantDesign/constants/plantDesignConstants';

export const DesignerVerifierDetails = (props) => {
  const savedDesignerVerifier = useSelector((state) => state.plantDesignVerifier);
  const application = useSelector((state) => state.application);
  const applicantDetails = useSelector((state) => state.plantDesignApplicantDetails);

  const {
    isDesigner: savedIsDesigner = '',
    designerDetails: savedDesignerDetails = {},
    verifierDetails: savedVerifierDetails = {},
    uploadedDocument: savedUploadedDocument = [],
  } = savedDesignerVerifier;

  const [isDesigner, setIsDesigner] = useState(savedIsDesigner);
  const [designerDetails, setDesignerDetails] = useState(savedDesignerDetails);
  const [verifierDetails, setVerifierDetails] = useState(savedVerifierDetails);
  const [verifierIsDesigner, setVerifierIsDesigner] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState(savedUploadedDocument);

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);
  const [designerErrorSummary, setDesignerErrorSummary] = useState([]);
  const [verifierErrorSummary, setVerifierErrorSummary] = useState([]);
  const [combinedErrorSummary, setCombinedErrorSummary] = useState([]);

  const designerDetailsRef = useRef();
  const verifierDetailsRef = useRef();

  useEffect(() => {
    if (showErrors) {
      validateAll();
    }
  }, [isDesigner, designerDetails, verifierDetails, uploadedDocument]);

  useEffect(() => {
    setCombinedErrorSummary([].concat(errorSummary, designerErrorSummary, verifierErrorSummary));
  }, [errorSummary, designerErrorSummary, verifierErrorSummary]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const validators = [
    {
      fieldName: 'isDesigner',
      fieldValue: isDesigner,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'uploadFile',
      fieldValue: uploadedDocument,
      isValid: (v) => v && v.length > 0,
      errorMessage: () => 'Please attach file',
    },
  ];

  const combineName = (firstName, middleName, lastName) => {
    return `${firstName?.toUpperCase()?.trim()} ${middleName?.toUpperCase()?.trim()} ${lastName
      ?.toUpperCase()
      ?.trim()}`.trim();
  };
  const designerIsVerifier = () => {
    const designer =
      isDesigner === 'yes'
        ? combineName(applicantDetails.firstName, applicantDetails.middleName, applicantDetails.lastName)
        : combineName(designerDetails.firstName, designerDetails.middleName, designerDetails.lastName);

    const verifier = combineName(verifierDetails.firstName, verifierDetails.middleName, verifierDetails.lastName);
    return designer === verifier;
  };

  const validateAll = () => {
    const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

    let designerComponentValid = true;
    if (isDesigner === 'no' && designerDetailsRef && designerDetailsRef.current)
      designerComponentValid = designerDetailsRef.current.validate();

    let verifierComponentValid = true;
    if (verifierDetailsRef && verifierDetailsRef.current)
      verifierComponentValid = verifierDetailsRef.current.validate();

    const verifierIsDesigner = designerIsVerifier();

    setVerifierIsDesigner(verifierIsDesigner);

    const isValid = isCurrentComponentValid && designerComponentValid && verifierComponentValid && !verifierIsDesigner;
    setShowErrors(!isValid);

    return isValid;
  };

  const onChangeIsDesigner = (v) => {
    if (v === 'yes') {
      setDesignerErrorSummary([]);
    }
    setIsDesigner(v);
  };

  const storeDetails = (isCompleted) => {
    const details = {
      isCompleted,
      isDesigner,
      verifierDetails,
      designerDetails,
      uploadedDocument,
    };
    dispatch(storeDesignerVerifierDetails(details));
  };

  const onContinueClick = () => {
    if (validateAll()) {
      storeDetails(true);

      setShowErrors(false);

      dispatch(saveApplication(true))
        .unwrap()
        .then(() => {
          navigate(PagePath.plantDesignReview);
        })
        .catch(() => {});
    } else {
      setShowErrors(true);
      window.scrollTo(0, 0);
    }
  };

  const onPreSaving = () => {
    let completed = validateAll();
    storeDetails(completed);
  };

  const onBackClick = () => {
    storeDetails(validateAll());
    navigate(PagePath.plantDesignDrawingAndTechnicalDetails);
  };

  const attachmentApi = getAttachmentApi(application.applicationId);

  return (
    <>
      {verifierIsDesigner && (
        <InPageAlert variant="error" title="The designer and design verifier cannot be the same person.">
          You will need to get the design verified by someone else.
        </InPageAlert>
      )}
      <ErrorSummary errors={combinedErrorSummary} />
      <SectionPage>
        <Row>
          <Col span={8}>
            <Heading level={2} style={{ marginTop: 0 }}>
              Designer details
            </Heading>
            <FormRadioGroup
              id="isDesigner"
              name="isDesigner"
              vertical={false}
              options={radioOptionsYesNo}
              onChange={onChangeIsDesigner}
              value={isDesigner}
              legend={PlantDesignDesignerVerifierPage.areYouDesignerLabel}
              helpMessage={PlantDesignDesignerVerifierPage.areYouDesignerHelpMessage}
              errorMessage={errors?.isDesigner?.errorMessage}
              hasError={showErrors && errors?.isDesigner?.invalid}
            />
            {isDesigner === 'no' && (
              <PersonDetails
                ref={designerDetailsRef}
                id="designer"
                onChange={setDesignerDetails}
                onErrorChange={setDesignerErrorSummary}
                value={designerDetails}
              />
            )}
          </Col>
        </Row>
      </SectionPage>

      {isDesigner && (
        <>
          <SectionPage>
            <Row>
              <Col span={8}>
                <Heading level={2}>Designer verifier details</Heading>
                <InPageAlert variant="info" title="Design verifier conditions">
                  <ul>
                    <li>
                      They have the skills, qualifications, competence and experience to design the plant item or verify
                      the design.
                    </li>
                    <li>They are not involved in the production of the design.</li>
                  </ul>
                </InPageAlert>
                <PersonDetails
                  ref={verifierDetailsRef}
                  id="verifier"
                  onChange={setVerifierDetails}
                  onErrorChange={setVerifierErrorSummary}
                  value={verifierDetails}
                  hasNameError={verifierIsDesigner}
                  nameErrorMessage="Name must be different to designer"
                />
              </Col>
            </Row>
          </SectionPage>
          <SectionPage>
            <Row>
              <Col span={8}>
                <Heading level={2}>Upload the Designer and Design Verifier statement</Heading>
                {uploadedDocument && uploadedDocument.length == 0 && (
                  <>
                    <Heading level={4} style={{ marginBottom: 0 }}>
                      Document requirements:
                    </Heading>
                    <ol>
                      <li>Documents must be in English.</li>
                      <li>
                        You can only submit electronic documents. If you are uploading a scanned copy of a print
                        document, check the document image is clear and all text is legible.
                      </li>
                    </ol>
                  </>
                )}
                {uploadedDocument && uploadedDocument.length > 0 && (
                  <Heading level={4} style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                    Uploaded:
                  </Heading>
                )}
                <div
                  id="uploadFile"
                  className={uploadedDocument && uploadedDocument.length > 0 ? styles.uploadedFile : styles.uploadFile}
                >
                  <FileUploadPattern
                    api={attachmentApi}
                    name="uploadFile"
                    id="uploadFile"
                    legendText=""
                    initialFiles={uploadedDocument}
                    onUploaded={(value) => setUploadedDocument(value)}
                    onRemoved={(value) => setUploadedDocument(value)}
                    maxMBSize={10}
                    acceptedFormats=".png,.jpg,.pdf"
                    errorMessage={errors.uploadFile?.errorMessage}
                  />
                </div>
                {uploadedDocument && uploadedDocument.length == 0 && (
                  <>
                    <Heading level={4} style={{ marginBottom: 0 }}>
                      If you do not have a completed Designer and verifier statement:
                    </Heading>
                    <ol>
                      <li>Download the Designer and verifier statement form now.</li>
                      <li>Arrange for the document to be completed and signed.</li>
                      <li>Upload the statement to this application.</li>
                    </ol>
                    <a href={PlantDesignConstants.designerAndVerifierStatementUrl} target="_blank" rel="noreferrer">
                      <IconDownload color="secondaryBlue" style={{ marginRight: '8px' }} />
                      Download the Designer and verifier statement
                    </a>
                  </>
                )}
              </Col>
            </Row>
          </SectionPage>
        </>
      )}

      <SectionPage>
        <PasswordlessContinueSaveExitButtons
          onBackClick={onBackClick}
          onContinueClick={onContinueClick}
          onPreSaving={onPreSaving}
          licenceType={PlantDesignConstants.licenceType}
        />
      </SectionPage>
    </>
  );
};
